import React, { useRef, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { rehypeLinkTypes } from "../rehypePlugin/rehypeLinkType";
import { markdownComponents } from "../MarkdownComponent";
import { TimelineItem } from "../utils/markdownParser";

type TimelineBlockProps = {
  items: TimelineItem[];
};

export const TimelineBlock: React.FC<TimelineBlockProps> = ({ items }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [linePosition, setLinePosition] = useState({ top: 0, bottom: 0 });

  useEffect(() => {
    if (!containerRef.current) return;
    const dots = containerRef.current.querySelectorAll(".timeline-dot");
    if (dots.length === 0) return;

    const firstDot = dots[0] as HTMLElement;
    const lastDot = dots[dots.length - 1] as HTMLElement;

    const containerRect = containerRef.current.getBoundingClientRect();
    const firstRect = firstDot.getBoundingClientRect();
    const lastRect = lastDot.getBoundingClientRect();

    setLinePosition({
      top: firstRect.top - containerRect.top + firstRect.height / 2,
      bottom:
        containerRect.height -
        (lastRect.top - containerRect.top + lastRect.height / 2),
    });
  }, [items]);

  return (
    <div ref={containerRef} className="relative">
      
      <div
        className="absolute left-2 w-1 bg-blue-500 -translate-x-1/2"
        style={{
          top: `${linePosition.top}px`,
          bottom: `${linePosition.bottom}px`,
        }}
      ></div>

      <div className="space-y-1 relative">
        {items.map((it, idx) => {
          const color = it.color || (idx === 0 ? "pink" : "gray");
          const dot = `bg-${color}-500`;
          const badge = `bg-${color}-100 text-${color}-700`;

          return (
            <div key={idx} className="relative flex items-center">
              <span
                className={`timeline-dot absolute left-2 top-1/2 w-4 h-4 ${dot} rounded-full border-4 border-white -translate-x-1/2 -translate-y-1/2 z-8`}
              />
              <div className="ml-4 w-[95%] bg-white rounded-xl shadow-md border border-gray-100 p-4 my-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="px-3 py-1 rounded-full text-xs bg-blue-100 text-blue-700 font-medium">
                    {it.time}
                  </span>
                  {it.badge && (
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${badge}`}>
                      {it.badge}
                    </span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-gray-800">{it.title}</h3>
                {it.subtitle && (
                  <div className="text-sm text-gray-600 mt-1">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeLinkTypes]}
                      components={markdownComponents}
                    >
                      {it.subtitle}
                    </ReactMarkdown>
                  </div>
                )}
                {it.extra && <p className="text-xs text-gray-400 mt-1">{it.extra}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
